import React, { useState, useEffect } from 'react';
import { 
  Settings as SettingsIcon, 
  Percent,
  Save,
  ToggleLeft,
  ToggleRight,
  IndianRupee
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { settingsAPI } from '../../services/api';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gstSettings, setGstSettings] = useState({
    percentage: 5,
    enabled: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.getGST();
      setGstSettings(response.data.gst);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateGST(gstSettings);
      toast.success('GST settings updated successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleGST = () => {
    setGstSettings(prev => ({ ...prev, enabled: !prev.enabled }));
  };

  // Calculate example breakdown
  const exampleRoomCharge = 1000;
  const exampleGST = gstSettings.enabled 
    ? Math.round((exampleRoomCharge * gstSettings.percentage) / 100) 
    : 0;
  const exampleTotal = exampleRoomCharge + exampleGST;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-display font-bold text-midnight-900">
            System Settings
          </h1>
          <p className="text-midnight-600 mt-1">
            Configure system-wide settings for billing and taxes
          </p>
        </div>

        {/* GST Settings Card */}
        <div className="card">
          <div className="card-header flex items-center gap-3">
            <div className="w-10 h-10 bg-railway-100 rounded-lg flex items-center justify-center">
              <Percent className="w-5 h-5 text-railway-600" />
            </div>
            <div>
              <h2 className="text-lg font-display font-semibold text-midnight-900">
                GST Configuration
              </h2>
              <p className="text-sm text-midnight-500">
                Configure Goods and Services Tax for room bookings
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Enable/Disable GST */}
            <div className="flex items-center justify-between p-4 bg-midnight-50 rounded-xl">
              <div>
                <h3 className="font-semibold text-midnight-900">Enable GST</h3>
                <p className="text-sm text-midnight-500">
                  Apply GST to all room bookings
                </p>
              </div>
              <button
                onClick={toggleGST}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  gstSettings.enabled 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-midnight-200 text-midnight-600'
                }`}
              >
                {gstSettings.enabled ? (
                  <>
                    <ToggleRight className="w-6 h-6" />
                    <span>Enabled</span>
                  </>
                ) : (
                  <>
                    <ToggleLeft className="w-6 h-6" />
                    <span>Disabled</span>
                  </>
                )}
              </button>
            </div>

            {/* GST Percentage */}
            <div className={gstSettings.enabled ? '' : 'opacity-50 pointer-events-none'}>
              <label className="block text-sm font-medium text-midnight-700 mb-2">
                GST Percentage
              </label>
              <div className="relative max-w-xs">
                <input
                  type="number"
                  value={gstSettings.percentage}
                  onChange={(e) => setGstSettings(prev => ({ 
                    ...prev, 
                    percentage: parseFloat(e.target.value) || 0 
                  }))}
                  min="0"
                  max="100"
                  step="0.1"
                  className="input-field pr-12"
                  disabled={!gstSettings.enabled}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-midnight-400 font-medium">
                  %
                </span>
              </div>
              <p className="text-xs text-midnight-500 mt-2">
                Standard GST rate in India is 12% for hotel rooms ₹1000-₹7500, and 18% for rooms above ₹7500
              </p>
            </div>

            {/* Example Calculation */}
            <div className="bg-gradient-to-r from-railway-50 to-railway-100 rounded-xl p-6 border border-railway-200">
              <h4 className="font-semibold text-railway-900 mb-4 flex items-center gap-2">
                <IndianRupee className="w-4 h-4" />
                Example Bill Breakdown
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-railway-700">Room Charges</span>
                  <span className="font-semibold text-railway-900">
                    ₹{exampleRoomCharge.toLocaleString('en-IN')}
                  </span>
                </div>
                {gstSettings.enabled && (
                  <div className="flex justify-between">
                    <span className="text-railway-700">GST @ {gstSettings.percentage}%</span>
                    <span className="font-semibold text-railway-900">
                      ₹{exampleGST.toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-railway-300">
                  <span className="font-bold text-railway-900">Total</span>
                  <span className="font-bold text-railway-900 text-lg">
                    ₹{exampleTotal.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-midnight-100">
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="card p-6 bg-blue-50 border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2">
            ℹ️ About GST in Retiring Rooms
          </h4>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>GST is calculated on the room charges (subtotal)</li>
            <li>The GST percentage you set will be applied to all new bookings</li>
            <li>Existing bookings will retain their original GST calculations</li>
            <li>Guests will see the GST breakdown in their booking confirmation and invoice</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Settings;
